/**
 * WidgetRecentlyPlayed — public, embeddable "Recently Played" widget.
 * Route: /widget/recently-played/:stationId  (no auth, iframe-friendly)
 * Polls the public API every 60s. Update #194.
 */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API_BASE } from "@/lib/api";
import { getStationTheme } from "@/lib/stationTheme";

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 90) return "just now";
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} hr ago`;
  return `${Math.round(s / 86400)} d ago`;
}

export default function WidgetRecentlyPlayed() {
  const { stationId } = useParams();
  const [data, setData] = useState(null);
  const [current, setCurrent] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    const load = () => {
      fetch(`${API_BASE}/public/nowplaying/${stationId}/recent?limit=10`)
        .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
        .then((d) => alive && setData(d))
        .catch(() => alive && setError("Station not found"));
      fetch(`${API_BASE}/public/nowplaying/${stationId}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => alive && d?.ok && setCurrent(d))
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [stationId]);

  const theme = getStationTheme(data?.genre);
  const accent = theme.hex;

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0d14] flex items-center justify-center text-sm text-gray-500 font-mono">{error}</div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0d14] text-gray-100 p-4 font-sans" data-testid="widget-recently-played">
      {/* header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 min-w-0">
          {data?.logo_url && (
            <img src={data.logo_url} alt="" className="h-8 w-8 rounded-md object-cover" />
          )}
          <div className="truncate font-semibold text-sm" data-testid="widget-station-name">{data?.station_name || "…"}</div>
        </div>
        <div className="flex items-center gap-1.5 rounded-full border px-2.5 py-1"
             style={{ borderColor: `${accent}66`, color: accent }}>
          <span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ backgroundColor: accent }} />
          <span className="font-mono text-[9px] uppercase tracking-[0.2em]">Live</span>
        </div>
      </div>

      {/* on-air hero */}
      {current?.track && (
        <div className="rounded-xl border p-3 mb-4 flex items-center gap-3"
             style={{ borderColor: `${accent}55`, background: `${accent}0d` }}
             data-testid="widget-onair">
          {current.track.artwork_url ? (
            <img src={current.track.artwork_url} alt="" className="h-14 w-14 rounded-lg object-cover" />
          ) : (
            <div className="h-14 w-14 rounded-lg" style={{ background: `${accent}33` }} />
          )}
          <div className="min-w-0">
            <div className="font-mono text-[9px] uppercase tracking-[0.2em] mb-0.5" style={{ color: accent }}>On air now</div>
            <div className="font-semibold text-sm truncate">{current.track.title || current.track.raw}</div>
            <div className="text-xs text-gray-400 truncate">{current.track.artist}</div>
          </div>
        </div>
      )}

      {/* recent list */}
      <div className="space-y-1.5" data-testid="widget-track-list">
        {(data?.tracks || []).map((t, i) => (
          <div key={i} className="flex items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-white/5 transition">
            {t.artwork_url ? (
              <img src={t.artwork_url} alt="" loading="lazy" className="h-10 w-10 rounded-md object-cover" />
            ) : (
              <div className="h-10 w-10 rounded-md bg-white/10 flex items-center justify-center text-gray-500 text-xs">♪</div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-medium truncate">{t.title || t.raw}</div>
              <div className="text-[11px] text-gray-500 truncate">{t.artist}</div>
            </div>
            <div className="font-mono text-[10px] text-gray-600 shrink-0">{timeAgo(t.detected_at)}</div>
          </div>
        ))}
        {data && (data.tracks || []).length === 0 && (
          <div className="text-center text-xs text-gray-500 py-8">No tracks detected yet.</div>
        )}
      </div>

      {(() => {
        const pricingUrl = `/?utm_source=widget&utm_medium=embed&utm_campaign=recently_played&utm_content=${stationId}#pricing`;
        return (
          <div className="mt-4 text-center space-y-1">
            <a href={pricingUrl} target="_blank" rel="noreferrer"
               data-testid="widget-powered-by"
               className="block font-mono text-[9px] uppercase tracking-[0.2em] text-gray-600 hover:text-gray-400 transition">
              Powered by BroadcastFlow AI
            </a>
            <a href={pricingUrl} target="_blank" rel="noreferrer"
               data-testid="widget-pricing-cta"
               className="inline-block text-[10px] font-medium transition opacity-70 hover:opacity-100"
               style={{ color: accent }}>
              Get this widget for your station →
            </a>
          </div>
        );
      })()}
    </div>
  );
}
